import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Chessground } from 'chessground';
import { Chess } from 'chess.js';
import './PGNViewerWithAnalysis.css';
import { Box, Button, ButtonGroup, Paper, Menu, MenuItem, Tooltip, Slider, Typography } from '@mui/material';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import FlipCameraAndroidIcon from '@mui/icons-material/FlipCameraAndroid';
import SpeedIcon from '@mui/icons-material/Speed';

export default function PGNViewerWithAnalysis({
    pgn,
    size,
    onPositionChange,
    currentPath,
    setCurrentPath,
    currentMoveIndex,
    setCurrentMoveIndex,
    topMovesPanel,
    boardOrientation = 'white',
    variations,
    setVariations,
    activeVariationIndex,
    setActiveVariationIndex,
    onFlipBoard,
    onToggleAnalysis,
    isAnalysisEnabled = false,
    isStockfishReady = true,
    analysisDepth = 25,
    onDepthChange,
    onDepthChangeCommitted,
    numberOfLines = 3,
    onLinesChange,
    isMobile = false
}) {
    const chessRef = useRef(new Chess());
    const [ground, setGround] = useState(null);
    const [moves, setMoves] = useState([]);
    const boardRef = useRef(null);
    const [variationPath, setVariationPath] = useState([]);
    const [startingFen, setStartingFen] = useState(null);
    const [contextMenu, setContextMenu] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const playIntervalRef = useRef(null);

    const movePanelWidth = isMobile
        ? size
        : Math.max(200, Math.min(300, size * 0.5));

    const movePanelHeight = isMobile ? 120 : size;

    const totalWidth = isMobile ? size : size + 16 + movePanelWidth;

    const updateBoardPosition = useCallback((fen, lastMove) => {
        if (!ground) return;

        const tempChess = new Chess(fen);
        const dests = new Map();

        const legalMoves = tempChess.moves({ verbose: true });
        legalMoves.forEach(move => {
            if (!dests.has(move.from)) {
                dests.set(move.from, []);
            }
            dests.get(move.from).push(move.to);
        });

        ground.set({
            fen: fen,
            lastMove: lastMove,
            turnColor: tempChess.turn() === 'w' ? 'white' : 'black',
            movable: {
                free: false,
                color: tempChess.turn() === 'w' ? 'white' : 'black',
                dests: dests,
                showDests: true
            },
            draggable: {
                enabled: true
            }
        });
    }, [ground]);

    useEffect(() => {
        if (ground) {
            ground.set({ orientation: boardOrientation });
        }
    }, [boardOrientation, ground]);

    useEffect(() => {
        if (!pgn || !Array.isArray(pgn)) return;

        const chess = chessRef.current;

        try {
            const pgnString = pgn.join('\n');
            chess.reset();
            chess.loadPgn(pgnString);

            const header = chess.header();
            const customFen = header.FEN || null;
            setStartingFen(customFen);

            const history = chess.history({ verbose: true });

            if (customFen) {
                chess.load(customFen);
            } else {
                chess.reset();
            }

            setMoves(history);
            setCurrentMoveIndex(0);
            setCurrentPath([]);
            setVariations({});
            setActiveVariationIndex(null);
            setVariationPath([]);

            if (boardRef.current && !ground) {
                const board = Chessground(boardRef.current, {
                    fen: chess.fen(),
                    orientation: boardOrientation,
                    coordinates: true,
                    turnColor: chess.turn() === 'w' ? 'white' : 'black',
                    movable: {
                        free: false,
                        color: chess.turn() === 'w' ? 'white' : 'black',
                        dests: new Map(),
                        showDests: true
                    },
                    draggable: {
                        enabled: true
                    },
                    events: {
                        move: (orig, dest) => {
                            try {
                                const move = chess.move({ from: orig, to: dest, promotion: 'q' });

                                if (move) {
                                    setCurrentPath(prev => [...prev, {
                                        move,
                                        fen: chess.fen(),
                                        variations: {}
                                    }]);
                                    setActiveVariationIndex(null);

                                    const dests = new Map();
                                    const legalMoves = chess.moves({ verbose: true });
                                    legalMoves.forEach(m => {
                                        if (!dests.has(m.from)) {
                                            dests.set(m.from, []);
                                        }
                                        dests.get(m.from).push(m.to);
                                    });

                                    board.set({
                                        fen: chess.fen(),
                                        lastMove: [orig, dest],
                                        turnColor: chess.turn() === 'w' ? 'white' : 'black',
                                        movable: {
                                            free: false,
                                            color: chess.turn() === 'w' ? 'white' : 'black',
                                            dests: dests,
                                            showDests: true
                                        }
                                    });
                                }
                            } catch (error) {
                                console.error('Invalid move:', error);
                            }
                        }
                    }
                });

                setGround(board);
            }
        } catch (error) {
            console.error('Error loading PGN:', error);
        }

        return () => {
            if (ground) {
                ground.destroy();
                setGround(null);
            }
        };
    }, [pgn]);

    useEffect(() => {
        if (onPositionChange && moves.length > 0) {
            const analysisChess = new Chess();

            if (startingFen) {
                analysisChess.load(startingFen);
            }

            for (let i = 0; i < currentMoveIndex; i++) {
                analysisChess.move(moves[i].san);
            }

            for (const pathMove of currentPath) {
                analysisChess.move(pathMove.move.san);
            }

            const currentFen = analysisChess.fen();
            onPositionChange(currentFen, boardOrientation);
        }
    }, [currentMoveIndex, currentPath, moves, onPositionChange, boardOrientation, startingFen]);

    useEffect(() => {
        if (ground && chessRef.current) {
            updateBoardPosition(chessRef.current.fen(), undefined);
        }
    }, [updateBoardPosition, ground]);

    const getCurrentVariations = useCallback(() => {
        if (variationPath.length === 0) {
            return variations[currentMoveIndex] || [];
        } else {
            let current = variations;
            for (const pathSegment of variationPath) {
                if (!current[pathSegment.moveIndex] || !current[pathSegment.moveIndex][pathSegment.varIndex]) {
                    return [];
                }
                const variation = current[pathSegment.moveIndex][pathSegment.varIndex];
                if (pathSegment.subMoveIndex >= variation.length) {
                    return [];
                }
                current = variation[pathSegment.subMoveIndex].variations;
            }
            return current[currentPath.length] || [];
        }
    }, [variations, currentMoveIndex, variationPath, currentPath]);

    const setCurrentVariations = useCallback((newVariations) => {
        if (variationPath.length === 0) {
            setVariations(prev => ({
                ...prev,
                [currentMoveIndex]: newVariations
            }));
        } else {
            setVariations(prev => {
                const newVars = JSON.parse(JSON.stringify(prev));
                let current = newVars;

                for (let i = 0; i < variationPath.length; i++) {
                    const pathSegment = variationPath[i];
                    if (i === variationPath.length - 1) {
                        const variation = current[pathSegment.moveIndex][pathSegment.varIndex];
                        variation[pathSegment.subMoveIndex].variations[currentPath.length] = newVariations;
                    } else {
                        current = current[pathSegment.moveIndex][pathSegment.varIndex][pathSegment.subMoveIndex].variations;
                    }
                }

                return newVars;
            });
        }
    }, [variationPath, currentMoveIndex, currentPath, setVariations]);

    const goToMove = useCallback((moveIndex) => {
        if (currentPath.length > 0 && activeVariationIndex === null) {
            const currentVars = getCurrentVariations();
            const variationExists = currentVars.some(v =>
                JSON.stringify(v.map(m => m.move.san)) === JSON.stringify(currentPath.map(m => m.move.san))
            );

            if (!variationExists) {
                setCurrentVariations([...currentVars, currentPath]);
            }
        }

        const chess = chessRef.current;

        if (startingFen) {
            chess.load(startingFen);
        } else {
            chess.reset();
        }

        for (let i = 0; i < moveIndex; i++) {
            chess.move(moves[i].san);
        }

        const lastMove = moveIndex > 0 ?
            [moves[moveIndex - 1].from, moves[moveIndex - 1].to] :
            undefined;

        updateBoardPosition(chess.fen(), lastMove);
        setCurrentMoveIndex(moveIndex);
        setCurrentPath([]);
        setActiveVariationIndex(null);
        setVariationPath([]);
    }, [moves, updateBoardPosition, setCurrentPath, setCurrentMoveIndex, currentPath, activeVariationIndex, getCurrentVariations, setCurrentVariations, startingFen]);

    const handleStart = useCallback(() => {
        goToMove(0);
    }, [goToMove]);

    const handlePrevious = useCallback(() => {
        if (currentPath.length > 0) {
            const newPath = currentPath.slice(0, -1);
            setCurrentPath(newPath);

            const chess = chessRef.current;

            if (startingFen) {
                chess.load(startingFen);
            } else {
                chess.reset();
            }

            for (let i = 0; i < currentMoveIndex; i++) {
                chess.move(moves[i].san);
            }
            for (const pathMove of newPath) {
                chess.move(pathMove.move.san);
            }

            const lastMoveSquares = newPath.length > 0
                ? [newPath[newPath.length - 1].move.from, newPath[newPath.length - 1].move.to]
                : (currentMoveIndex > 0 ? [moves[currentMoveIndex - 1].from, moves[currentMoveIndex - 1].to] : undefined);

            updateBoardPosition(chess.fen(), lastMoveSquares);

            if (newPath.length === 0 && activeVariationIndex !== null) {
                setActiveVariationIndex(null);
            }
        } else if (currentMoveIndex > 0) {
            goToMove(currentMoveIndex - 1);
        }
    }, [currentMoveIndex, currentPath, moves, goToMove, updateBoardPosition, setCurrentPath, activeVariationIndex, setActiveVariationIndex, startingFen]);

    const handleNext = useCallback(() => {
        const currentVars = getCurrentVariations();

        if (activeVariationIndex !== null && currentMoveIndex > 0) {
            const variation = currentVars[activeVariationIndex];
            if (variation && currentPath.length < variation.length) {
                const newPath = variation.slice(0, currentPath.length + 1);
                setCurrentPath(newPath);

                const chess = chessRef.current;

                if (startingFen) {
                    chess.load(startingFen);
                } else {
                    chess.reset();
                }

                for (let i = 0; i < currentMoveIndex; i++) {
                    chess.move(moves[i].san);
                }
                for (const pathMove of newPath) {
                    chess.move(pathMove.move.san);
                }

                updateBoardPosition(chess.fen(), [newPath[newPath.length - 1].move.from, newPath[newPath.length - 1].move.to]);
                return;
            }
        }

        if (currentPath.length === 0 && currentMoveIndex < moves.length) {
            goToMove(currentMoveIndex + 1);
        }
    }, [currentMoveIndex, currentPath, moves.length, goToMove, activeVariationIndex, getCurrentVariations, updateBoardPosition, setCurrentPath, moves, startingFen]);

    const handleEnd = useCallback(() => {
        goToMove(moves.length);
    }, [moves.length, goToMove]);

    const handlePlay = useCallback(() => {
        if (isPlaying) {
            if (playIntervalRef.current) {
                clearInterval(playIntervalRef.current);
                playIntervalRef.current = null;
            }
            setIsPlaying(false);
        } else {
            setIsPlaying(true);
            playIntervalRef.current = setInterval(() => {
                setCurrentMoveIndex(prev => {
                    if (prev >= moves.length) {
                        if (playIntervalRef.current) {
                            clearInterval(playIntervalRef.current);
                            playIntervalRef.current = null;
                        }
                        setIsPlaying(false);
                        return prev;
                    }

                    const nextIndex = prev + 1;
                    const chess = chessRef.current;

                    if (startingFen) {
                        chess.load(startingFen);
                    } else {
                        chess.reset();
                    }

                    for (let i = 0; i < nextIndex; i++) {
                        chess.move(moves[i].san);
                    }

                    const lastMove = nextIndex > 0 ?
                        [moves[nextIndex - 1].from, moves[nextIndex - 1].to] :
                        undefined;

                    updateBoardPosition(chess.fen(), lastMove);

                    return nextIndex;
                });
            }, playbackSpeed * 1000);
        }
    }, [isPlaying, moves, startingFen, updateBoardPosition, playbackSpeed]);

    useEffect(() => {
        if (isPlaying && playIntervalRef.current) {
            clearInterval(playIntervalRef.current);
            playIntervalRef.current = setInterval(() => {
                setCurrentMoveIndex(prev => {
                    if (prev >= moves.length) {
                        if (playIntervalRef.current) {
                            clearInterval(playIntervalRef.current);
                            playIntervalRef.current = null;
                        }
                        setIsPlaying(false);
                        return prev;
                    }

                    const nextIndex = prev + 1;
                    const chess = chessRef.current;

                    if (startingFen) {
                        chess.load(startingFen);
                    } else {
                        chess.reset();
                    }

                    for (let i = 0; i < nextIndex; i++) {
                        chess.move(moves[i].san);
                    }

                    const lastMove = nextIndex > 0 ?
                        [moves[nextIndex - 1].from, moves[nextIndex - 1].to] :
                        undefined;

                    updateBoardPosition(chess.fen(), lastMove);

                    return nextIndex;
                });
            }, playbackSpeed * 1000);
        }
    }, [playbackSpeed, isPlaying, moves, startingFen, updateBoardPosition]);

    useEffect(() => {
        return () => {
            if (playIntervalRef.current) {
                clearInterval(playIntervalRef.current);
            }
        };
    }, []);

    const deleteLine = useCallback(() => {
        if (activeVariationIndex !== null) {
            const currentVars = getCurrentVariations();
            const newVars = currentVars.filter((_, idx) => idx !== activeVariationIndex);
            setCurrentVariations(newVars);
            setCurrentPath([]);
            setActiveVariationIndex(null);
            goToMove(currentMoveIndex);
        }
        setContextMenu(null);
    }, [currentMoveIndex, goToMove, activeVariationIndex, getCurrentVariations, setCurrentVariations, setCurrentPath]);

    const loadVariation = useCallback((varIndex, moveIdx, isNested = false) => {
        const currentVars = getCurrentVariations();
        const variation = currentVars[varIndex];
        if (!variation) return;

        const newPath = variation.slice(0, moveIdx + 1);
        setCurrentPath(newPath);
        setActiveVariationIndex(varIndex);

        if (isNested) {
            setVariationPath(prev => [...prev, {
                moveIndex: currentMoveIndex,
                varIndex: varIndex,
                subMoveIndex: moveIdx
            }]);
        }

        const chess = chessRef.current;

        if (startingFen) {
            chess.load(startingFen);
        } else {
            chess.reset();
        }

        for (let i = 0; i < currentMoveIndex; i++) {
            chess.move(moves[i].san);
        }
        for (const pathMove of newPath) {
            chess.move(pathMove.move.san);
        }

        const lastMoveSquares = [newPath[newPath.length - 1].move.from, newPath[newPath.length - 1].move.to];
        updateBoardPosition(chess.fen(), lastMoveSquares);
    }, [getCurrentVariations, currentMoveIndex, moves, updateBoardPosition, setCurrentPath, setActiveVariationIndex, setVariationPath, startingFen]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            switch (event.key) {
                case 'ArrowLeft':
                    event.preventDefault();
                    handlePrevious();
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    handleNext();
                    break;
                case 'Home':
                    event.preventDefault();
                    handleStart();
                    break;
                case 'End':
                    event.preventDefault();
                    handleEnd();
                    break;
                case 'Delete':
                    event.preventDefault();
                    if (activeVariationIndex !== null) {
                        deleteLine();
                    }
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handlePrevious, handleNext, handleStart, handleEnd, deleteLine, activeVariationIndex]);

    const renderVariations = useCallback((vars, startIndex, depth = 0) => {
        if (!vars || vars.length === 0) return '';

        let text = '';

        vars.forEach((variation, varIndex) => {
            const isActiveVar = activeVariationIndex === varIndex && depth === variationPath.length;
            text += `<span style="color:${isActiveVar ? '#f57c00' : depth === 0 ? '#9c27b0' : '#4caf50'};font-weight:bold">( `;

            variation.forEach((pathMove, idx) => {
                const moveNum = Math.floor((startIndex + idx) / 2) + 1;
                const isWhite = (startIndex + idx) % 2 === 0;

                if (isWhite) {
                    text += `${moveNum}. `;
                } else if (idx === 0) {
                    text += `${moveNum}... `;
                }

                const isCurrentVarMove = isActiveVar && idx === currentPath.length - 1;
                const moveStyle = isCurrentVarMove ? 'background-color:#2196F3;color:white;' : '';

                text += `<span data-var-index="${varIndex}" data-var-move="${idx}" data-var-base="${startIndex}" data-depth="${depth}" style="cursor:pointer;padding:2px;border-radius:3px;${moveStyle}">${pathMove.move.san}</span> `;

                if (pathMove.variations && Object.keys(pathMove.variations).length > 0) {
                    for (const subVarKey in pathMove.variations) {
                        text += renderVariations(pathMove.variations[subVarKey], startIndex + idx + 1, depth + 1);
                    }
                }
            });
            text += ') </span>';
        });

        return text;
    }, [activeVariationIndex, variationPath, currentPath]);

    const renderMoveText = () => {
        let text = '';

        for (let i = 0; i < moves.length; i++) {
            const move = moves[i];
            const moveNumber = Math.floor(i / 2) + 1;
            const isWhiteMove = i % 2 === 0;

            if (isWhiteMove) {
                text += `${moveNumber}. `;
            }

            const isCurrentMove = i === currentMoveIndex - 1 && currentPath.length === 0;
            const moveStyle = {
                cursor: 'pointer',
                padding: '2px 4px',
                borderRadius: '3px',
                backgroundColor: isCurrentMove ? '#2196F3' : 'transparent',
                color: isCurrentMove ? 'white' : 'inherit',
                fontWeight: isCurrentMove ? 'bold' : 'normal'
            };

            text += `<span data-move-index="${i}" style="${Object.entries(moveStyle).map(([k, v]) => `${k.replace(/[A-Z]/g, m => '-' + m.toLowerCase())}:${v}`).join(';')}">${move.san}</span> `;

            if (variations[i]) {
                text += renderVariations(variations[i], i, 0);
            }

            if (i === currentMoveIndex - 1 && currentPath.length > 0 && activeVariationIndex === null) {
                text += '<span style="color:#f57c00;font-weight:bold">( ';
                currentPath.forEach((pathMove, idx) => {
                    const moveNum = Math.floor((currentMoveIndex + idx) / 2) + 1;
                    const isWhite = (currentMoveIndex + idx) % 2 === 0;
                    if (isWhite) {
                        text += `${moveNum}. `;
                    } else if (idx === 0) {
                        text += `${moveNum}... `;
                    }
                    text += `${pathMove.move.san} `;
                });
                text += ') </span>';
            }
        }

        return text;
    };

    const handleMoveClick = (e) => {
        if (e.button === 2) return;

        const moveIndex = e.target.getAttribute('data-move-index');
        if (moveIndex !== null) {
            goToMove(parseInt(moveIndex) + 1);
            return;
        }

        const varIndex = e.target.getAttribute('data-var-index');
        const varMove = e.target.getAttribute('data-var-move');
        const varBase = e.target.getAttribute('data-var-base');
        const depth = e.target.getAttribute('data-depth');

        if (varIndex !== null && varMove !== null && varBase !== null) {
            const baseIndex = parseInt(varBase);
            const isNested = parseInt(depth) > 0;

            if (currentMoveIndex !== baseIndex) {
                goToMove(baseIndex);
                setTimeout(() => {
                    loadVariation(parseInt(varIndex), parseInt(varMove), isNested);
                }, 50);
            } else {
                loadVariation(parseInt(varIndex), parseInt(varMove), isNested);
            }
        }
    };

    const handleContextMenu = (e) => {
        e.preventDefault();

        const varIndex = e.target.getAttribute('data-var-index');

        if (varIndex !== null) {
            setContextMenu({
                mouseX: e.clientX,
                mouseY: e.clientY,
                varIndex: parseInt(varIndex)
            });

            setActiveVariationIndex(parseInt(varIndex));
        }
    };

    const handleCloseContextMenu = () => {
        setContextMenu(null);
    };

    const handlePlaybackSpeedChange = useCallback((event, newValue) => {
        setPlaybackSpeed(newValue);
    }, []);

    // Handler for analysis button that works even when disabled
    const handleAnalysisClick = useCallback(() => {
        if (isStockfishReady && onToggleAnalysis) {
            onToggleAnalysis();
        }
    }, [isStockfishReady, onToggleAnalysis]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 0.5 : 1, alignItems: 'center' }}>
            {/* Main content: Board + Move Panel side by side (desktop) or stacked (mobile) */}
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 1 : 2,
                alignItems: isMobile ? 'center' : 'flex-start',
                justifyContent: 'center'
            }}>
                {/* Chess Board Column - contains board and controls on desktop */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: isMobile ? 0.5 : 1,
                    width: isMobile ? '100%' : 'auto'  // Add this line
                }}>
                    {/* Chess Board */}
                    <div
                        ref={boardRef}
                        className="blue merida"
                        style={{
                            width: `${size}px`,
                            height: `${size}px`,
                            boxShadow: '0 0 20px rgba(33, 150, 243, 0.5)',
                            border: '3px solid #2196F3',
                            margin: isMobile ? '0 auto' : undefined  // Add this for centering
                        }}
                    />

                    {/* Controls under the board (desktop only) */}
                    {!isMobile && (
                        <>
                            {/* Row 1: Integrated Button Group (Flip + Navigation + Analysis) */}
                            <Box sx={{
                                width: size,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <ButtonGroup variant="contained" size="small">
                                    {onFlipBoard && (
                                        <Tooltip title="Flip Board">
                                            <Button onClick={onFlipBoard} sx={{ px: 1 }}>
                                                <FlipCameraAndroidIcon fontSize="small" />
                                                <Box component="span" sx={{ ml: 0.5 }}>Flip</Box>
                                            </Button>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Go to start">
                                        <Button
                                            onClick={handleStart}
                                            disabled={currentMoveIndex === 0 && currentPath.length === 0}
                                            sx={{ px: 1 }}
                                        >
                                            <SkipPreviousIcon fontSize="small" />
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Previous move">
                                        <Button
                                            onClick={handlePrevious}
                                            disabled={currentMoveIndex === 0 && currentPath.length === 0}
                                            sx={{ px: 1 }}
                                        >
                                            <NavigateBeforeIcon fontSize="small" />
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title={isPlaying ? "Pause" : "Play"}>
                                        <Button
                                            onClick={handlePlay}
                                            color={isPlaying ? 'secondary' : 'primary'}
                                            sx={{ px: 1 }}
                                        >
                                            {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Next move">
                                        <Button
                                            onClick={handleNext}
                                            disabled={currentMoveIndex === moves.length && currentPath.length === 0}
                                            sx={{ px: 1 }}
                                        >
                                            <NavigateNextIcon fontSize="small" />
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Go to end">
                                        <Button
                                            onClick={handleEnd}
                                            disabled={currentMoveIndex === moves.length}
                                            sx={{ px: 1 }}
                                        >
                                            <SkipNextIcon fontSize="small" />
                                        </Button>
                                    </Tooltip>
                                    {onToggleAnalysis && (
                                        <Tooltip title={!isStockfishReady ? "Loading Stockfish..." : isAnalysisEnabled ? "Stop Analysis" : "Start Analysis"}>
                                            <Button
                                                onClick={handleAnalysisClick}
                                                disabled={!isStockfishReady}
                                                color={isAnalysisEnabled ? 'error' : 'primary'}
                                                sx={{ px: 1 }}
                                            >
                                                <SpeedIcon fontSize="small" />
                                                <Box component="span" sx={{ ml: 0.5 }}>{isAnalysisEnabled ? 'Stop' : 'Analyze'}</Box>
                                            </Button>
                                        </Tooltip>
                                    )}
                                </ButtonGroup>
                            </Box>

                            {/* Row 2: Sliders (Speed, Depth, Lines) */}
                            <Box sx={{
                                width: size,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                                flexWrap: 'wrap'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: 32 }}>
                                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap', fontSize: '12px' }}>
                                        Speed:
                                    </Typography>
                                    <Slider
                                        value={playbackSpeed}
                                        onChange={handlePlaybackSpeedChange}
                                        min={1}
                                        max={20}
                                        step={1}
                                        size="small"
                                        valueLabelDisplay="auto"
                                        valueLabelFormat={(v) => `${v}s`}
                                        sx={{ width: 70 }}
                                    />
                                    <Typography variant="caption" sx={{ minWidth: 24, ml: 0.5, fontSize: '12px' }}>
                                        {playbackSpeed}s
                                    </Typography>
                                </Box>

                                {onDepthChange && (
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        opacity: isAnalysisEnabled ? 1 : 0.5,
                                        height: 32
                                    }}>
                                        <Typography variant="caption" sx={{ whiteSpace: 'nowrap', fontSize: '12px' }}>
                                            Depth:
                                        </Typography>
                                        <Slider
                                            value={analysisDepth}
                                            onChange={onDepthChange}
                                            onChangeCommitted={onDepthChangeCommitted}
                                            min={5}
                                            max={50}
                                            step={1}
                                            size="small"
                                            valueLabelDisplay="auto"
                                            disabled={!isAnalysisEnabled}
                                            sx={{ width: 60 }}
                                        />
                                        <Typography variant="caption" sx={{ minWidth: 20, ml: 0.5, fontSize: '12px' }}>
                                            {analysisDepth}
                                        </Typography>
                                    </Box>
                                )}

                                {onLinesChange && (
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        opacity: isAnalysisEnabled ? 1 : 0.5,
                                        height: 32
                                    }}>
                                        <Typography variant="caption" sx={{ whiteSpace: 'nowrap', fontSize: '12px' }}>
                                            Lines:
                                        </Typography>
                                        <Slider
                                            value={numberOfLines}
                                            onChange={onLinesChange}
                                            min={1}
                                            max={6}
                                            step={1}
                                            size="small"
                                            valueLabelDisplay="auto"
                                            disabled={!isAnalysisEnabled}
                                            sx={{ width: 40 }}
                                        />
                                        <Typography variant="caption" sx={{ minWidth: 12, ml: 0.5, fontSize: '12px' }}>
                                            {numberOfLines}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </>
                    )}
                </Box>

                {/* Move List Panel */}
                <Paper
                    elevation={3}
                    sx={{
                        width: movePanelWidth,
                        height: movePanelHeight,
                        overflow: 'auto',
                        p: isMobile ? 1 : 2,
                        fontFamily: 'monospace',
                        fontSize: isMobile ? '12px' : '14px',
                        lineHeight: isMobile ? 1.6 : 1.8,
                        cursor: 'default',
                        userSelect: 'none'
                    }}
                    onClick={handleMoveClick}
                    onContextMenu={handleContextMenu}
                    dangerouslySetInnerHTML={{ __html: renderMoveText() }}
                />
            </Box>

            {/* Mobile Controls */}
            {isMobile && (
                <>
                    <Box sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 0.5
                    }}>
                        <ButtonGroup variant="contained" size="small">
                            {onFlipBoard && (
                                <Tooltip title="Flip Board">
                                    <Button onClick={onFlipBoard} sx={{ minWidth: '36px', px: 0.5 }}>
                                        <FlipCameraAndroidIcon fontSize="small" />
                                    </Button>
                                </Tooltip>
                            )}
                            <Tooltip title="Go to start">
                                <Button
                                    onClick={handleStart}
                                    disabled={currentMoveIndex === 0 && currentPath.length === 0}
                                    sx={{ minWidth: '36px', px: 0.5 }}
                                >
                                    <SkipPreviousIcon fontSize="small" />
                                </Button>
                            </Tooltip>
                            <Tooltip title="Previous move">
                                <Button
                                    onClick={handlePrevious}
                                    disabled={currentMoveIndex === 0 && currentPath.length === 0}
                                    sx={{ minWidth: '36px', px: 0.5 }}
                                >
                                    <NavigateBeforeIcon fontSize="small" />
                                </Button>
                            </Tooltip>
                            <Tooltip title={isPlaying ? "Pause" : "Play"}>
                                <Button
                                    onClick={handlePlay}
                                    color={isPlaying ? 'secondary' : 'primary'}
                                    sx={{ minWidth: '36px', px: 0.5 }}
                                >
                                    {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                                </Button>
                            </Tooltip>
                            <Tooltip title="Next move">
                                <Button
                                    onClick={handleNext}
                                    disabled={currentMoveIndex === moves.length && currentPath.length === 0}
                                    sx={{ minWidth: '36px', px: 0.5 }}
                                >
                                    <NavigateNextIcon fontSize="small" />
                                </Button>
                            </Tooltip>
                            <Tooltip title="Go to end">
                                <Button
                                    onClick={handleEnd}
                                    disabled={currentMoveIndex === moves.length}
                                    sx={{ minWidth: '36px', px: 0.5 }}
                                >
                                    <SkipNextIcon fontSize="small" />
                                </Button>
                            </Tooltip>
                            {onToggleAnalysis && (
                                <Tooltip title={!isStockfishReady ? "Loading Stockfish..." : isAnalysisEnabled ? "Stop Analysis" : "Start Analysis"}>
                                    <Button
                                        onClick={handleAnalysisClick}
                                        disabled={!isStockfishReady}
                                        color={isAnalysisEnabled ? 'error' : 'primary'}
                                        sx={{ minWidth: '36px', px: 0.5 }}
                                    >
                                        <SpeedIcon fontSize="small" />
                                    </Button>
                                </Tooltip>
                            )}
                        </ButtonGroup>
                    </Box>

                    <Box sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        px: 0.5
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: 32 }}>
                            <Typography variant="caption" sx={{ whiteSpace: 'nowrap', fontSize: '10px' }}>
                                Spd:
                            </Typography>
                            <Slider
                                value={playbackSpeed}
                                onChange={handlePlaybackSpeedChange}
                                min={1}
                                max={20}
                                step={1}
                                size="small"
                                valueLabelDisplay="auto"
                                valueLabelFormat={(v) => `${v}s`}
                                sx={{ width: 50 }}
                            />
                            <Typography variant="caption" sx={{ minWidth: 18, ml: 0.5, fontSize: '10px' }}>
                                {playbackSpeed}s
                            </Typography>
                        </Box>

                        {onDepthChange && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                opacity: isAnalysisEnabled ? 1 : 0.5,
                                height: 32
                            }}>
                                <Typography variant="caption" sx={{ whiteSpace: 'nowrap', fontSize: '10px' }}>
                                    D:
                                </Typography>
                                <Slider
                                    value={analysisDepth}
                                    onChange={onDepthChange}
                                    onChangeCommitted={onDepthChangeCommitted}
                                    min={5}
                                    max={50}
                                    step={1}
                                    size="small"
                                    valueLabelDisplay="auto"
                                    disabled={!isAnalysisEnabled}
                                    sx={{ width: 50 }}
                                />
                                <Typography variant="caption" sx={{ minWidth: 16, ml: 0.5, fontSize: '10px' }}>
                                    {analysisDepth}
                                </Typography>
                            </Box>
                        )}

                        {onLinesChange && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                opacity: isAnalysisEnabled ? 1 : 0.5,
                                height: 32
                            }}>
                                <Typography variant="caption" sx={{ whiteSpace: 'nowrap', fontSize: '10px' }}>
                                    L:
                                </Typography>
                                <Slider
                                    value={numberOfLines}
                                    onChange={onLinesChange}
                                    min={1}
                                    max={6}
                                    step={1}
                                    size="small"
                                    valueLabelDisplay="auto"
                                    disabled={!isAnalysisEnabled}
                                    sx={{ width: 35 }}
                                />
                                <Typography variant="caption" sx={{ minWidth: 10, ml: 0.5, fontSize: '10px' }}>
                                    {numberOfLines}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </>
            )}

            {topMovesPanel && (
                <Box sx={{ width: isMobile ? '100%' : totalWidth, maxWidth: '100%', display: 'flex', justifyContent: 'center', px: isMobile ? 0.5 : 0 }}>
                    {topMovesPanel}
                </Box>
            )}

            <Menu
                open={contextMenu !== null}
                onClose={handleCloseContextMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
            >
                <MenuItem onClick={deleteLine}>Delete Variation</MenuItem>
            </Menu>
        </Box>
    );
}